import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_connectors: number
  active_connectors: number
  custom_connectors: number
  total_connections: number
  data_synced_tb: number
  avg_sync_time_sec: number
}

interface Connector {
  id: string
  name: string
  category: string
  connections: number
  data_synced_gb: number
  status: string
}

interface Category {
  name: string
  connectors: number
  connections: number
}

export default function ConnectorLibraryPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [connectors, setConnectors] = useState<Connector[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, connectorsRes, categoriesRes] = await Promise.all([
          fetch('/api/connectorlibrary/statistics'),
          fetch('/api/connectorlibrary/connectors'),
          fetch('/api/connectorlibrary/categories')
        ])
        setStatistics(await statsRes.json())
        setConnectors(await connectorsRes.json())
        setCategories(await categoriesRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'Active': 'bg-green-100 text-green-800',
      'Beta': 'bg-yellow-100 text-yellow-800',
      'Deprecated': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Connector Library...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Connector Library</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_connectors}</div>
              <div className="text-sm text-gray-500">Total Connectors</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.active_connectors}</div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.custom_connectors}</div>
              <div className="text-sm text-gray-500">Custom</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{statistics.total_connections.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Connections</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.data_synced_tb}TB</div>
              <div className="text-sm text-gray-500">Data Synced</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.avg_sync_time_sec}s</div>
              <div className="text-sm text-gray-500">Avg Sync Time</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Available Connectors</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Connector</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Connections</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Data Synced</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {connectors.map((conn) => (
                    <tr key={conn.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{conn.name}</td>
                      <td className="px-4 py-3 text-gray-600">{conn.category}</td>
                      <td className="px-4 py-3 text-gray-600">{conn.connections}</td>
                      <td className="px-4 py-3 text-gray-600">{conn.data_synced_gb}GB</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(conn.status)}`}>
                          {conn.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Categories</h2></div>
            <div className="p-4 space-y-3">
              {categories.map((cat, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium">{cat.name}</div>
                  <div className="text-sm text-gray-500">{cat.connectors} Connectors | {cat.connections} Connections</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
